import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackToTop from '../../components/elements/BackToTop';
import Header from '../../components/Layout/Header';
import navImg1 from '../../assets/images/resources/logo-1.png';
import Footer from '../../components/Layout/Footer';
import {
  clearBuyerSession,
  downloadBuyerOrderTicketsPdf,
  fetchBuyerInstallments,
  fetchBuyerMe,
  fetchBuyerTickets,
  getBuyerEmail,
  getBuyerToken,
  payNextBuyerInstallment,
  qrImageUrl,
} from '../../services/buyerTicketsApi';
import {
  fetchOrderStatus,
  refreshPublicPaymentStatus,
} from '../../services/publicEventsApi';
import './MesTickets.css';
import '../event-details/EventDetails.css';

function formatDateShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatMoney(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '0 XAF';
  return `${new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits: 0,
  }).format(Math.round(n))} XAF`;
}

function statusClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'VALID') return 'mes-tickets-ticket__status--valid';
  if (s === 'USED') return 'mes-tickets-ticket__status--used';
  return 'mes-tickets-ticket__status--other';
}

function statusLabel(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'VALID') return 'Valide';
  if (s === 'USED') return 'Utilisé';
  if (s === 'EXPIRED') return 'Expiré';
  return s || '—';
}

function initialsFromEmail(email) {
  const local = String(email || '')
    .split('@')[0]
    .replace(/[^a-zA-Z0-9]/g, '');
  if (!local) return '?';
  return local.slice(0, 2).toUpperCase();
}

function paymentMethodsForEvent(methods) {
  if (methods === 'ORANGE') return ['OM'];
  if (methods === 'MOMO') return ['MO'];
  return ['OM', 'MO'];
}

const PENDING_PAY_KEY = 'mboapass_pending_payment_mes_tickets';

function readPendingPay() {
  try {
    const raw = localStorage.getItem(PENDING_PAY_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writePendingPay(snapshot) {
  try {
    localStorage.setItem(PENDING_PAY_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore */
  }
}

function clearPendingPay() {
  try {
    localStorage.removeItem(PENDING_PAY_KEY);
  } catch {
    /* ignore */
  }
}

const MesTicketsListe = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState(getBuyerEmail());
  const [groups, setGroups] = useState([]);
  const [openInstallments, setOpenInstallments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const [payOrder, setPayOrder] = useState(null);
  const [payMode, setPayMode] = useState('next'); // 'next' | 'remaining'
  const [payMethod, setPayMethod] = useState('OM');
  const [payPhone, setPayPhone] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payInProgress, setPayInProgress] = useState(false);
  const [payBaselinePaid, setPayBaselinePaid] = useState(0);
  const [payError, setPayError] = useState('');
  const [payHints, setPayHints] = useState(null);
  const [paySuccess, setPaySuccess] = useState('');
  const [pdfDownloadingId, setPdfDownloadingId] = useState('');
  const [pdfError, setPdfError] = useState('');

  useEffect(() => {
    const handleScroll = () => setIsVisible(window.scrollY > 300);
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, []);

  const logout = useCallback(() => {
    clearBuyerSession();
    navigate('/mes-tickets', { replace: true });
  }, [navigate]);

  const toggleOrder = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const reloadAll = useCallback(async () => {
    const [me, tickets, installments] = await Promise.all([
      fetchBuyerMe(),
      fetchBuyerTickets(),
      fetchBuyerInstallments(),
    ]);
    if (me?.email) setEmail(me.email);
    setGroups(Array.isArray(tickets) ? tickets : []);
    setOpenInstallments(Array.isArray(installments) ? installments : []);
  }, []);

  useEffect(() => {
    if (!getBuyerToken()) {
      navigate('/mes-tickets', { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        await reloadAll();
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 401) {
          clearBuyerSession();
          navigate('/mes-tickets', { replace: true });
          return;
        }
        setError(
          err?.message || 'Impossible de charger vos billets pour le moment.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, reloadAll]);

  const closePayModal = useCallback(() => {
    clearPendingPay();
    setPayOrder(null);
    setPayMode('next');
    setPayError('');
    setPayHints(null);
    setPaySubmitting(false);
    setPayInProgress(false);
    setPayBaselinePaid(0);
  }, []);

  const openPayModal = useCallback((order, mode = 'next') => {
    clearPendingPay();
    setPayOrder(order);
    setPayMode(mode === 'remaining' ? 'remaining' : 'next');
    setPayError('');
    setPayHints(null);
    setPayInProgress(false);
    setPayBaselinePaid(Number(order?.amountPaid || 0));
    const methods = paymentMethodsForEvent(order?.event?.paymentMethods);
    setPayMethod(methods[0] || 'OM');
  }, []);

  const applySettledPayment = useCallback(
    async (st, baselinePaid) => {
      if (!st) return false;
      if (st.status === 'PAID') {
        clearPendingPay();
        setPayInProgress(false);
        setPaySuccess(
          'Solde réglé. Vos billets QR sont disponibles ci-dessous.',
        );
        setPayOrder(null);
        await reloadAll();
        return true;
      }
      const paid = Number(st.amount_paid || 0);
      if (st.status === 'PARTIALLY_PAID' && paid > Number(baselinePaid || 0)) {
        clearPendingPay();
        setPayInProgress(false);
        setPaySuccess(
          `Paiement reçu : ${formatMoney(paid)}. Reste : ${formatMoney(
            Math.max(0, Number(st.total_amount || 0) - paid),
          )}.`,
        );
        setPayOrder(null);
        await reloadAll();
        return true;
      }
      return false;
    },
    [reloadAll],
  );

  const markPayFailed = useCallback((message) => {
    clearPendingPay();
    setPayInProgress(false);
    setPayError(
      message ||
        'Le paiement a échoué ou a été annulé. Vérifiez le numéro et réessayez.',
    );
  }, []);

  const syncPayStatus = useCallback(
    async (orderId, baselinePaid) => {
      let providerStatus = '';
      try {
        const refreshed = await refreshPublicPaymentStatus(orderId);
        providerStatus = String(
          refreshed?.providerStatus || refreshed?.raw?.status || '',
        ).toUpperCase();
      } catch {
        /* webhook / Campay peut déjà avoir confirmé en base */
      }

      const st = await fetchOrderStatus(orderId);
      if (await applySettledPayment(st, baselinePaid)) {
        return { settled: true, st, providerStatus };
      }

      // Échec Mobile Money : la commande reste PARTIALLY_PAID — il faut
      // regarder le statut Campay (sinon la modale reste bloquée « en cours »).
      if (
        ['FAILED', 'FAIL', 'ERROR', 'CANCELLED', 'DECLINED'].includes(
          providerStatus,
        )
      ) {
        markPayFailed();
        return { settled: true, st, providerStatus, failed: true };
      }

      if (st?.status === 'FAILED') {
        markPayFailed();
        return { settled: true, st, providerStatus, failed: true };
      }

      return { settled: false, st, providerStatus };
    },
    [applySettledPayment, markPayFailed],
  );

  const handlePayNext = async (e) => {
    e.preventDefault();
    if (!payOrder?.orderId || paySubmitting || payInProgress) return;
    if (!payPhone.trim()) {
      setPayError('Indiquez le numéro Mobile Money à débiter.');
      return;
    }
    setPaySubmitting(true);
    setPayError('');
    setPaySuccess('');
    try {
      const baseline = Number(payOrder.amountPaid || 0);
      setPayBaselinePaid(baseline);
      const res = await payNextBuyerInstallment(payOrder.orderId, {
        paymentMethod: payMethod,
        phone: payPhone.trim(),
        settleRemaining: payMode === 'remaining',
      });
      setPayHints(res || null);
      writePendingPay({
        orderId: payOrder.orderId,
        baselinePaid: baseline,
        payMode,
        amount: res?.amount ?? null,
        eventTitle: payOrder.event?.title || null,
        startedAt: new Date().toISOString(),
      });
      setPayInProgress(true);
    } catch (err) {
      clearPendingPay();
      setPayError(err?.message || 'Impossible d’initier le paiement.');
      setPayInProgress(false);
    } finally {
      setPaySubmitting(false);
    }
  };

  const handleVerifyPay = async () => {
    if (!payOrder?.orderId || paySubmitting) return;
    setPayError('');
    setPaySubmitting(true);
    try {
      const result = await syncPayStatus(payOrder.orderId, payBaselinePaid);
      if (!result.settled) {
        setPayError(
          'Paiement toujours en attente. Validez sur votre téléphone puis réessayez.',
        );
      }
    } catch (err) {
      setPayError(
        err?.message || 'Vérification impossible. Réessayez dans quelques secondes.',
      );
    } finally {
      setPaySubmitting(false);
    }
  };

  const handleDownloadPdf = useCallback(async (orderId) => {
    if (!orderId || pdfDownloadingId) return;
    setPdfError('');
    setPdfDownloadingId(orderId);
    try {
      await downloadBuyerOrderTicketsPdf(orderId);
    } catch (err) {
      if (err?.status === 401) {
        clearBuyerSession();
        navigate('/mes-tickets', { replace: true });
        return;
      }
      setPdfError(
        err?.message || 'Impossible de télécharger le PDF pour le moment.',
      );
    } finally {
      setPdfDownloadingId('');
    }
  }, [navigate, pdfDownloadingId]);

  // Webhook + synchro Campay. Important : un échec Mobile Money ne passe PAS
  // la commande en FAILED (elle reste PARTIALLY_PAID) — on lit providerStatus.
  useEffect(() => {
    if (!payInProgress || !payOrder?.orderId) return undefined;
    const orderId = payOrder.orderId;
    const baselinePaid = payBaselinePaid;

    const tick = async () => {
      try {
        await syncPayStatus(orderId, baselinePaid);
      } catch {
        /* silent poll */
      }
    };

    const interval = setInterval(tick, 4000);
    tick();
    return () => clearInterval(interval);
  }, [payInProgress, payOrder?.orderId, payBaselinePaid, syncPayStatus]);

  // Reprise après refresh / retour sur la page
  useEffect(() => {
    if (loading || payInProgress || payOrder) return;
    const pending = readPendingPay();
    if (!pending?.orderId) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await syncPayStatus(
          pending.orderId,
          Number(pending.baselinePaid || 0),
        );
        if (cancelled || result.settled) return;

        const st = result.st;
        if (st?.status === 'PENDING' || st?.status === 'PARTIALLY_PAID') {
          const fromList = openInstallments.find(
            (o) => o.orderId === pending.orderId,
          );
          const restored =
            fromList ||
            ({
              orderId: pending.orderId,
              amountPaid: Number(pending.baselinePaid || 0),
              totalAmount: Number(st.total_amount || 0),
              remainingAmount: Math.max(
                0,
                Number(st.total_amount || 0) - Number(st.amount_paid || 0),
              ),
              event: { title: pending.eventTitle || 'Événement' },
              nextInstallment: st.next_installment
                ? {
                    amount: st.next_installment.amount,
                    label: st.next_installment.label,
                  }
                : null,
            });
          setPayOrder(restored);
          setPayMode(pending.payMode === 'remaining' ? 'remaining' : 'next');
          setPayBaselinePaid(Number(pending.baselinePaid || 0));
          setPayInProgress(true);
          setPayError('');
        } else {
          clearPendingPay();
        }
      } catch {
        /* garder le pending pour une prochaine tentative */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loading,
    openInstallments,
    payInProgress,
    payOrder,
    syncPayStatus,
  ]);

  const stats = useMemo(() => {
    const ticketCount = groups.reduce(
      (sum, g) => sum + (g.tickets?.length || 0),
      0,
    );
    const eventCount = new Set(groups.map((g) => g.event?.id).filter(Boolean))
      .size;
    const validCount = groups.reduce(
      (sum, g) =>
        sum +
        (g.tickets || []).filter(
          (t) => String(t.status || '').toUpperCase() === 'VALID',
        ).length,
      0,
    );
    return { ticketCount, eventCount, validCount };
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => {
      const title = String(g.event?.title || '').toLowerCase();
      const location = String(g.event?.location || '').toLowerCase();
      const buyer = String(g.buyerName || '').toLowerCase();
      const codes = (g.tickets || [])
        .map((t) => String(t.code || '').toLowerCase())
        .join(' ');
      return (
        title.includes(q) ||
        location.includes(q) ||
        buyer.includes(q) ||
        codes.includes(q)
      );
    });
  }, [groups, query]);

  const filteredInstallments = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return openInstallments;
    return openInstallments.filter((o) => {
      const title = String(o.event?.title || '').toLowerCase();
      const location = String(o.event?.location || '').toLowerCase();
      return title.includes(q) || location.includes(q);
    });
  }, [openInstallments, query]);

  const isEmpty =
    !loading &&
    !error &&
    groups.length === 0 &&
    openInstallments.length === 0;

  const allowedPayMethods = paymentMethodsForEvent(
    payOrder?.event?.paymentMethods,
  );

  const payModalCharge = useMemo(() => {
    if (!payOrder) return null;
    if (payMode === 'remaining') {
      return {
        amount: Number(payOrder.remainingAmount || 0),
        label: 'Solde restant',
        resteAfter: 0,
      };
    }
    const next = payOrder.nextInstallment;
    const nextAmt = Number(next?.amount || 0);
    const remaining = Number(payOrder.remainingAmount || 0);
    return {
      amount: nextAmt,
      label: next?.label || 'Tranche',
      resteAfter: Math.max(0, remaining - nextAmt),
    };
  }, [payOrder, payMode]);

  return (
    <React.Fragment>
      <Header
        navImg={navImg1}
        parentMenu="MesTickets"
        activeMenu="/mes-tickets"
      />
      <div className="mt-dash">
        <div className="container">
          <div className="mt-dash__grid">
            <aside className="mt-dash__sidebar">
              <div className="mt-dash__profile">
                <div className="mt-dash__avatar" aria-hidden="true">
                  {initialsFromEmail(email)}
                </div>
                <p className="mt-dash__eyebrow">Profil acheteur</p>
                <h1 className="mt-dash__name">Mes Tickets</h1>
                <p className="mt-dash__email">{email || '—'}</p>

                <div className="mt-dash__stats">
                  <div className="mt-dash__stat">
                    <strong>{loading ? '—' : stats.ticketCount}</strong>
                    <span>Billets</span>
                  </div>
                  <div className="mt-dash__stat">
                    <strong>{loading ? '—' : stats.eventCount}</strong>
                    <span>Événements</span>
                  </div>
                  <div className="mt-dash__stat">
                    <strong>{loading ? '—' : stats.validCount}</strong>
                    <span>Valides</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="mt-dash__logout"
                  onClick={logout}
                >
                  Déconnexion
                </button>
              </div>
            </aside>

            <main className="mt-dash__main">
              <div className="mt-dash__toolbar">
                <div>
                  <h2 className="mt-dash__title">Mes commandes</h2>
                  <p className="mt-dash__subtitle">
                    Retrouvez vos billets et finalisez vos paiements
                  </p>
                </div>
                <div className="mt-dash__search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher un événement…"
                    aria-label="Rechercher un événement"
                  />
                </div>
              </div>

              <div className="mt-dash__scroll">
                {loading ? (
                  <p className="mt-dash__loading">Chargement de vos billets…</p>
                ) : null}

                {!loading && error ? (
                  <div className="mt-dash__error" role="alert">
                    {error}
                  </div>
                ) : null}

                {paySuccess ? (
                  <div className="mt-dash__success" role="status">
                    {paySuccess}
                  </div>
                ) : null}

                {pdfError ? (
                  <div className="mt-dash__error" role="alert">
                    {pdfError}
                  </div>
                ) : null}

                {!loading && !error && filteredInstallments.length > 0 ? (
                  <section className="mt-installments">
                    
                    {filteredInstallments.map((order) => {
                      const pct =
                        order.totalAmount > 0
                          ? Math.min(
                              100,
                              Math.round(
                                (Number(order.amountPaid || 0) /
                                  Number(order.totalAmount)) *
                                  100,
                              ),
                            )
                          : 0;
                      const next = order.nextInstallment;
                      return (
                        <article
                          key={order.orderId}
                          className="mt-installment-card"
                        >
                          <div className="mt-installment-card__header">
                            {order.event?.posterUrl ? (
                              <img
                                className="mt-order__poster"
                                src={order.event.posterUrl}
                                alt=""
                              />
                            ) : (
                              <div className="mt-order__poster mt-order__poster--placeholder" />
                            )}
                            <div className="mt-order__meta">
                              <h3>{order.event?.title || 'Événement'}</h3>
                              <div className="mt-order__facts">
                                {formatDateShort(order.event?.startDate) ? (
                                  <span>
                                    <i
                                      className="far fa-calendar-alt"
                                      aria-hidden="true"
                                    />
                                    {formatDateShort(order.event.startDate)}
                                  </span>
                                ) : null}
                                <span>
                                  Payé {formatMoney(order.amountPaid)} /{' '}
                                  {formatMoney(order.totalAmount)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div
                            className="mt-installment-card__bar"
                            aria-hidden="true"
                          >
                            <span style={{ width: `${pct}%` }} />
                          </div>
                          <p className="mt-installment-card__progress">
                            {pct}% réglé
                            {next
                              ? ` · Prochaine échéance : ${next.label} (${formatMoney(next.amount)})`
                              : ''}
                          </p>
                          {next ? (
                            <div className="mt-installment-card__actions">
                              <button
                                type="button"
                                className="mt-installment-card__cta"
                                onClick={() => openPayModal(order, 'next')}
                              >
                                Payer la prochaine tranche
                              </button>
                              {Number(order.remainingAmount || 0) >
                              Number(next.amount || 0) ? (
                                <button
                                  type="button"
                                  className="mt-installment-card__cta mt-installment-card__cta--secondary"
                                  onClick={() =>
                                    openPayModal(order, 'remaining')
                                  }
                                >
                                  Payer le reste (
                                  {formatMoney(order.remainingAmount)})
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </section>
                ) : null}

                {isEmpty ? (
                  <div className="mt-dash__empty">
                    <div className="mt-dash__empty-icon" aria-hidden="true">
                      <i className="fas fa-ticket-alt" />
                    </div>
                    <p>Aucun billet pour le moment.</p>
                    <p>Ils apparaîtront ici après un paiement réussi.</p>
                    <Link to="/events" className="mt-dash__empty-link">
                      Parcourir les événements
                    </Link>
                  </div>
                ) : null}

                {!loading &&
                !error &&
                groups.length > 0 &&
                filteredGroups.length === 0 &&
                filteredInstallments.length === 0 ? (
                  <div className="mt-dash__empty">
                    <p>Aucun événement ne correspond à « {query} ».</p>
                    <button
                      type="button"
                      className="mt-dash__empty-link"
                      onClick={() => setQuery('')}
                    >
                      Effacer la recherche
                    </button>
                  </div>
                ) : null}

                {!loading &&
                  !error &&
                  filteredGroups.map((group) => {
                    const key = `${group.orderId}-${group.event?.id}`;
                    const ticketCount = group.tickets?.length || 0;
                    const open = expandedKeys.has(key);
                    return (
                      <article
                        key={key}
                        className={`mt-order${open ? ' mt-order--open' : ''}`}
                      >
                        <div className="mt-order__shell">
                          {group.event?.posterUrl ? (
                            <img
                              className="mt-order__poster"
                              src={group.event.posterUrl}
                              alt=""
                            />
                          ) : (
                            <div className="mt-order__poster mt-order__poster--placeholder" />
                          )}
                          <div className="mt-order__body">
                            <button
                              type="button"
                              className="mt-order__toggle"
                              onClick={() => toggleOrder(key)}
                              aria-expanded={open}
                            >
                              <div className="mt-order__header">
                                <div className="mt-order__meta">
                                  <div className="mt-order__title-row">
                                    <h3>{group.event?.title || 'Événement'}</h3>
                                    <span className="mt-order__badge">
                                      {ticketCount} billet
                                      {ticketCount > 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <div className="mt-order__facts">
                                    {formatDateShort(group.event?.startDate) ? (
                                      <span>
                                        <i
                                          className="far fa-calendar-alt"
                                          aria-hidden="true"
                                        />
                                        {formatDateShort(group.event.startDate)}
                                      </span>
                                    ) : null}
                                    {group.event?.location ? (
                                      <span>
                                        <i
                                          className="fas fa-map-marker-alt"
                                          aria-hidden="true"
                                        />
                                        {group.event.location}
                                      </span>
                                    ) : null}
                                    <span>
                                      <i
                                        className="fas fa-receipt"
                                        aria-hidden="true"
                                      />
                                      Achat {formatDateShort(group.purchaseDate)}
                                      {group.buyerName
                                        ? ` · ${group.buyerName}`
                                        : ''}
                                    </span>
                                  </div>
                                </div>
                                <span
                                  className="mt-order__chevron"
                                  aria-hidden="true"
                                >
                                  <i
                                    className={`fas fa-chevron-${open ? 'up' : 'down'}`}
                                  />
                                </span>
                              </div>
                            </button>

                            <div className="mt-order__actions">
                              {group.event?.slug ? (
                                <Link
                                  className="mt-order__event-link"
                                  to={`/${encodeURIComponent(group.event.slug)}`}
                                >
                                  Voir l’événement
                                </Link>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        {open ? (
                          <div className="mt-order__tickets">
                            <div className="mt-order__tickets-toolbar">
                              <p className="mt-order__tickets-label">
                                {(group.tickets || []).length} billet
                                {(group.tickets || []).length > 1 ? 's' : ''}
                              </p>
                              <button
                                type="button"
                                className="mt-order__pdf-btn"
                                disabled={pdfDownloadingId === group.orderId}
                                onClick={() => handleDownloadPdf(group.orderId)}
                              >
                                <i className="fas fa-file-pdf" aria-hidden="true" />
                                {pdfDownloadingId === group.orderId
                                  ? 'Préparation…'
                                  : 'Télécharger le Ticket'}
                              </button>
                            </div>
                            <ul className="mt-order__tickets-list">
                              {(group.tickets || []).map((ticket) => (
                                <li key={ticket.id} className="mt-ticket">
                                  <div className="mt-ticket__info">
                                    <div className="mt-ticket__top">
                                      <strong className="mt-ticket__type">
                                        {ticket.typeLabel || 'Billet'}
                                      </strong>
                                      <span
                                        className={`mes-tickets-ticket__status ${statusClass(
                                          ticket.status,
                                        )}`}
                                      >
                                        {statusLabel(ticket.status)}
                                      </span>
                                    </div>
                                    <span className="mt-ticket__code">
                                      {ticket.code}
                                    </span>
                                  </div>
                                  <img
                                    className="mt-ticket__qr"
                                    src={qrImageUrl(
                                      ticket.qrData || ticket.code,
                                    )}
                                    alt={`QR ${ticket.code}`}
                                    width={96}
                                    height={96}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
              </div>
            </main>
          </div>
        </div>
      </div>

      {payOrder ? (
        <div
          className="checkout-modal-overlay"
          onClick={closePayModal}
        >
          <div
            className="checkout-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Finaliser le paiement"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="checkout-modal__close"
              aria-label={payInProgress ? 'Annuler' : 'Fermer'}
              onClick={closePayModal}
            >
              ×
            </button>

            <h3 className="checkout-panel__title mb-2">
              Finaliser le paiement
            </h3>
            <p className="checkout-panel__subtitle mb-3">
              {payOrder.event?.title || 'Événement'}
            </p>

            <div className="checkout-alert checkout-alert--info">
              {payModalCharge
                ? `Paiement : ${formatMoney(payModalCharge.amount)} - ${payModalCharge.label} . Reste ${formatMoney(payModalCharge.resteAfter)}`
                : '—'}
            </div>

            {payInProgress ? (
              <div className="checkout-alert checkout-alert--info" role="status">
                Transaction en cours... Veuillez patienter ou annuler.
              </div>
            ) : null}

            {payError ? (
              <div className="checkout-alert checkout-alert--error" role="alert">
                {payError}
              </div>
            ) : null}

            <form onSubmit={handlePayNext} className="checkout-form">
              <label>Moyen de paiement</label>
              <div className="checkout-pay-methods">
                {allowedPayMethods.includes('OM') ? (
                  <button
                    type="button"
                    className={`checkout-pay-method${payMethod === 'OM' ? ' checkout-pay-method--active' : ''}`}
                    onClick={() => setPayMethod('OM')}
                    disabled={payInProgress}
                  >
                    Orange Money
                  </button>
                ) : null}
                {allowedPayMethods.includes('MO') ? (
                  <button
                    type="button"
                    className={`checkout-pay-method${payMethod === 'MO' ? ' checkout-pay-method--active' : ''}`}
                    onClick={() => setPayMethod('MO')}
                    disabled={payInProgress}
                  >
                    MTN MoMo
                  </button>
                ) : null}
              </div>

              <label htmlFor="mt-pay-phone">Numéro à débiter *</label>
              <input
                id="mt-pay-phone"
                type="tel"
                value={payPhone}
                onChange={(e) => setPayPhone(e.target.value)}
                required
                disabled={payInProgress}
                placeholder="+237 6…"
              />

              <button
                type="submit"
                className="checkout-btn-primary thm-btn"
                disabled={paySubmitting || payInProgress}
              >
                {paySubmitting || payInProgress
                  ? 'Transaction en cours...'
                  : payMode === 'remaining'
                    ? 'Payer le reste'
                    : 'Initier le paiement'}
              </button>
            </form>

            {payInProgress ? (
              <button
                type="button"
                className="checkout-secondary-btn mt-2"
                onClick={handleVerifyPay}
                disabled={paySubmitting}
              >
                {paySubmitting
                  ? 'Vérification…'
                  : 'J’ai payé — vérifier le statut'}
              </button>
            ) : null}

            {payInProgress ? (
              <button
                type="button"
                className="checkout-secondary-btn mt-2"
                onClick={closePayModal}
              >
                Annuler
              </button>
            ) : null}

            {payHints?.ussdCode ? (
              <div className="checkout-alert checkout-alert--info mt-2">
                Validez la commande sur votre téléphone ou composez le{' '}
                <strong>{payHints.ussdCode}</strong>
              </div>
            ) : null}
            {payHints?.paymentUrl ? (
              <a
                href={payHints.paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="schedule-one__btn thm-btn d-inline-flex align-items-center justify-content-center w-100 mt-2"
                style={{ textDecoration: 'none' }}
              >
                Ouvrir la page de paiement
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      <BackToTop scroll={isVisible} />
      <Footer />
    </React.Fragment>
  );
};

export default MesTicketsListe;
